import { Box, Paper, Typography } from "@mui/material";
import { ReactNode, useState } from "react";
import {
	BundleAction,
	BundleDetail,
	FinishedGroup,
	ScrapAction,
	ScrapEntry,
} from "../../models/FinishedBundle";
import { formatWeight } from "../../utils/utils";
import { HeatBundlesPanel } from "./HeatBundlesPanel";
import { HeatList } from "./HeatList";
import { SummaryItem, toHeatGroups } from "./StackerReviewComponents";

type Props = {
	groups: FinishedGroup[];
	details: BundleDetail[];
	scrap: ScrapEntry[];
	canEdit: boolean;
	isMutating: boolean;
	isTotalsLoading: boolean;
	remark: ReactNode;
	shiftId?: number;
	shift: { start: Date; end: Date } | null;
	shiftHeatIds: number[];
	adding: boolean;
	onAddingChange: (adding: boolean) => void;
	onAction: (action: BundleAction) => void;
	onScrapAction: (action: ScrapAction) => void;
};

export const StackerReviewGrid = ({
	groups,
	details,
	scrap,
	canEdit,
	isMutating,
	isTotalsLoading,
	remark,
	shiftId,
	shift,
	shiftHeatIds,
	adding,
	onAddingChange,
	onAction,
	onScrapAction,
}: Props) => {
	const [selectedKey, setSelectedKey] = useState<string | null>(null);

	const heats = toHeatGroups(groups);
	const heat = heats.find((h) => h.key === selectedKey) ?? heats[0] ?? null;
	const heatRows = heat
		? details
				.filter((d) => d.GroupKey === heat.key)
				.sort(
					(a, b) =>
						new Date(a.Timestamp).getTime() - new Date(b.Timestamp).getTime(),
				)
		: [];
	const heatScrap = heat
		? scrap.filter((e) => e.ScrapKey === heat.scrapKey)
		: [];

	const disabled = !canEdit || isMutating;
	const allShiftHeatIds = [
		...shiftHeatIds,
		...groups.flatMap((g) => (g.HeatId ? [g.HeatId] : [])),
	];
	// From the bundles, rounded once on display: the groups' weights are already rounded to 2 decimals and their sum
	// drifts from the Shift Report (which sums the bundles)
	const totalWeight = details
		.filter((d) => d.IsActive)
		.reduce((s, d) => s + (d.Tons || 0), 0);
	const goodBundles = heats.reduce((s, h) => s + h.bundles, 0);
	const goodPieces = heats.reduce((s, h) => s + h.pieces, 0);
	const scrapPieces = scrap.reduce((s, e) => s + e.Pieces, 0);
	const scrapTons = scrap.reduce((s, e) => s + (e.Tons || 0), 0);

	return (
		<Box
			sx={{
				flex: "1 1 0",
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				gap: 2,
			}}
			data-testid="stacker-review-grid"
		>
			<Box
				sx={{
					flex: "1 1 0",
					minHeight: 0,
					display: "grid",
					gridTemplateColumns: {
						xs: "minmax(0, 1fr)",
						md: "minmax(0, 11fr) minmax(0, 9fr)",
					},
					gap: 2,
				}}
			>
				<Box
					sx={{
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						gap: 2,
					}}
				>
					<HeatList
						heats={heats}
						details={details}
						selectedKey={heat?.key ?? null}
						canEdit={canEdit}
						disabled={disabled}
						adding={adding}
						shiftId={shiftId}
						shiftHeatIds={allShiftHeatIds}
						onSelect={setSelectedKey}
						onAddingChange={onAddingChange}
						onAction={onAction}
						summary={
							<>
								<SummaryItem
									label="Finished"
									value={`${formatWeight(totalWeight)} t`}
									loading={isTotalsLoading}
									testId="finished-total-weight"
								/>
								<SummaryItem
									label="Bundles"
									value={goodBundles.toLocaleString()}
									loading={isTotalsLoading}
									testId="finished-total-bundles"
								/>
								<SummaryItem
									label="Pieces"
									value={goodPieces.toLocaleString()}
									loading={isTotalsLoading}
									testId="finished-total-pieces"
								/>
								<SummaryItem
									label="Scrap"
									value={
										scrapPieces
											? `${scrapPieces.toLocaleString()} pcs · ${formatWeight(scrapTons)} t`
											: "-"
									}
									loading={isTotalsLoading}
									testId="finished-total-scrap"
								/>
							</>
						}
					/>
					<Box sx={{ flexShrink: 0 }}>{remark}</Box>
				</Box>

				{heat ? (
					<HeatBundlesPanel
						key={heat.key}
						heat={heat}
						rows={heatRows}
						scrap={heatScrap}
						canEdit={canEdit}
						disabled={disabled}
						shift={shift}
						shiftId={shiftId}
						shiftHeatIds={allShiftHeatIds}
						shiftBundles={details}
						onAction={onAction}
						onScrapAction={onScrapAction}
					/>
				) : (
					<Paper
						elevation={2}
						sx={{
							alignSelf: "start",
							p: 4,
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: 2,
						}}
					>
						<Typography variant="body2" color="text.secondary">
							No bundles recorded for this shift yet.
						</Typography>
					</Paper>
				)}
			</Box>
		</Box>
	);
};
